import React, { useEffect } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import {
	Controller,
	useFieldArray,
	useFormContext,
	useWatch,
} from 'react-hook-form';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { AddIcon, RemoveIcon } from 'components/icons';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';
import { setTourGate } from 'components/onboarding/tour/tourGate';

const MANAGE_SITES_TOUR_KEY = 'canpark_manage_sites';

interface NameZoneSectionProps {
	loading: boolean;
}

const NameZoneSection: React.FC<NameZoneSectionProps> = ({ loading }) => {
	const {
		control,
		formState: { errors },
	} = useFormContext<ParkingLocation>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'parkingZoneOptions',
	});

	// During the Manage Sites tour, hold the NAME / ZONE steps' Next button until
	// the respective field is filled (the first zone is the required address).
	const nameValue = useWatch({ control, name: 'name' });
	const firstZoneValue = useWatch({
		control,
		name: 'parkingZoneOptions.0.name',
	});

	useEffect(() => {
		if (getActiveTourKey() !== MANAGE_SITES_TOUR_KEY) return;
		setTourGate('manage-sites-name', !!nameValue && nameValue.trim().length > 0);
	}, [nameValue]);

	useEffect(() => {
		if (getActiveTourKey() !== MANAGE_SITES_TOUR_KEY) return;
		setTourGate(
			'manage-sites-zone',
			!!firstZoneValue && firstZoneValue.trim().length > 0,
		);
	}, [firstZoneValue]);

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			<Typography variant="body1" fontWeight={300} color="primary">
				Name and Zones
			</Typography>

			{/* Main Parking Lot Name */}
			<Controller
				control={control}
				name="name"
				rules={{ required: 'Parking lot name is required' }}
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						data-tour="parking-lot-name-field"
						label="Parking Lot Name"
						fullWidth
						autoFocus
						disabled={loading}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
					/>
				)}
			/>

			{/* Zones */}
			<Box data-tour="zone-address-fields" display="flex" flexDirection="column" gap={1}>
				{fields.map((field, index) => (
					<Box
						key={field.id}
						display="flex"
						alignItems="center"
						gap={1}
					>
						{index > 0 && (
							<Tooltip title="Remove Zone" placement="top">
								<IconButton
									onClick={() => remove(index)}
									disabled={loading}
								>
									<RemoveIcon />
								</IconButton>
							</Tooltip>
						)}

						<Controller
							control={control}
							name={`parkingZoneOptions.${index}.name`}
							rules={{
								required: 'Zone name is required',
								validate: (value) =>
									value.trim() !== '' ||
									'Zone name cannot be empty',
							}}
							render={({ field, fieldState }) => (
								<TextField
									{...field}
									label={`Zone/Address ${index + 1}`}
									fullWidth
									disabled={loading}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
								/>
							)}
						/>
					</Box>
				))}
			</Box>
			<Box display="flex" gap={1}>
				{/* Add Zone Button */}
				<Box>
					<Tooltip title="Add Zone/Address" placement="top">
						<IconButton
							onClick={() => append({ id: 0, name: '' })}
							disabled={loading}
						>
							<AddIcon />
						</IconButton>
					</Tooltip>
				</Box>
				<Typography variant="caption" color="text.secondary">
					Officers can select a zone or address from the predefined
					list.
					<br />
					<Typography variant="caption" color="secondary">
						Note: They can still modify the selection or enter a
						custom zone or address.
					</Typography>
				</Typography>
			</Box>
		</Box>
	);
};

export default NameZoneSection;
